import React from 'react';

import validate from '@vlabs/shared/validators';
import {
  Page, Control, Divider, Grid, GridRow, GridCol,
} from '@vlabs/uikit';
import cn from 'classnames';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './MatchPolicyForm.module.sass';
import { SearchByFaces } from './SearchByFaces.tsx';

function MatchPolicyForm({
  onSubmit,
  isSelectedMatch = false,
  matchPolicyList = [],
  initialValues = {},
}) {
  const { t } = useTranslation();
  const isFaceDescriptorExtract = useWatch({ name: 'extract.faceDescriptor' });
  const initialListId = initialValues.candidates?.list_id || initialValues.list?.value;
  const formMethods = useForm({
    defaultValues: {
      ...initialValues,
      candidates: {
        origin: 'faces',
        ...initialValues.candidates,
        list_id: initialListId,
      },
    },
  });
  const {
    getValues,
    handleSubmit: originalHandleSubmit,
    register,
    setError,
    watch,
    formState: { errors },
  } = formMethods;
  const selectedListName = watch('label');

  const validateMatchPolicyLabel = (value) => {
    const isLabelExist = matchPolicyList.some(
      ({ label }) => label === value && label !== initialValues.label,
    );

    if (!isLabelExist) return true;
    return t('Параметр сравнения с таким названием уже существует в рамках данного сценария');
  };

  const handleSubmit = (submit) => (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    originalHandleSubmit(submit)(event);
  };

  const submitMatchPolicy = (values) => {
    const label = getValues('label');
    const labelValidationResult = validateMatchPolicyLabel(label);

    if (labelValidationResult !== true) {
      setError('label', {
        type: 'validate',
        message: labelValidationResult,
      });
      return;
    }

    const matchPolicy = {
      label,
      candidates: {
        origin: 'faces',
        list_id: values.candidates.list_id,
      },
    };
    const limit = values.limit === '' ? undefined : Number(values.limit);
    const threshold = values.threshold === '' ? undefined : Number(values.threshold);

    if (limit !== undefined) matchPolicy.limit = limit;
    if (threshold !== undefined) matchPolicy.threshold = threshold;
    if (values.targets !== undefined) matchPolicy.targets = values.targets;

    onSubmit(matchPolicy);
  };

  let pageTitle = t('handlers:добавить новый параметр сравнения');
  let submitButtonText = t('кнопка.добавить');
  if (isSelectedMatch) {
    pageTitle = t('handlers:изменить параметр сравнения');
    submitButtonText = t('кнопка.изменить');
  }

  let labelErrorMessage;
  if (selectedListName) {
    const validationResult = validateMatchPolicyLabel(selectedListName);
    if (validationResult !== true) labelErrorMessage = validationResult;
  }

  return (
    <FormProvider {...formMethods}>
      <Page
        className={st.Page}
        title={pageTitle}
      >
        <form onSubmit={handleSubmit(submitMatchPolicy)}>
          <div className={cn(st.BlocksWrapper, st.BlocksWrapper_flex)}>
            <div className={st.Subtitle}>
              {t('handlers:policies.match_policy.label')}
            </div>
            <div className={st.Width_1}>
              <Control.Input
                disabled
                errorMessage={labelErrorMessage}
                value={selectedListName || ''}
              />
            </div>
          </div>

          <div className={st.BlocksWrapper}>
            <div className={st.Subtitle}>
              {t('handlers:выполнять поиск по')}
            </div>
            <Divider small />
            {!!isFaceDescriptorExtract && <SearchByFaces />}
            {!isFaceDescriptorExtract && (
              <div>
                {t('handlers:policies.match.enable biometric face template extraction')}
              </div>
            )}
          </div>

          <Divider small />
          <div className={st.BlocksWrapper}>
            <div className={st.Subtitle}>
              {t('handlers:дополнительные параметры поиска')}
            </div>
            <Divider small />
            <Grid>
              <GridRow>
                <GridCol>
                  <Control.Input
                    label={t('handlers:максимальное количество похожих в результатах поиска')}
                    {...register('limit', {
                      min: validate.gte(1),
                      max: validate.lte(100),
                      validate: {
                        isInteger: (value) => Number.isInteger(Number(value)) || t('валидация.число.целое'),
                      },
                    })}
                    errors={errors}
                    placeholder={t('не задано')}
                  />
                </GridCol>
                <GridCol>
                  <Control.Input
                    label={t('handlers:пороговое значение точности')}
                    {...register('threshold', {
                      min: validate.gte(0),
                      max: validate.lte(1),
                      validate: validate.number(),
                    })}
                    errors={errors}
                    placeholder={t('не задано')}
                  />
                </GridCol>
              </GridRow>
            </Grid>
          </div>

          <Divider small />
          <Control.Button
            disabled={!isFaceDescriptorExtract}
            fullWidth
            type="submit"
          >
            {submitButtonText}
          </Control.Button>
        </form>
      </Page>
    </FormProvider>
  );
}

export default MatchPolicyForm;
